import moment from 'moment';
import {
  FilterType,
  MapOptionType,
  nsnType,
  SelectOptionType,
  Target,
  TargetType,
} from '../../utils/Types';
import { isAPISuccess, isValidString } from '../../utils/Util';
import { postTargetList } from '../../features/setTargetSlice';
import { ThunkDispatch } from 'redux-thunk';
import {
  DEFAULT_OPTION_LOCATION_SET_TARGET,
  TARGET_FILTER_INIT,
} from '../../utils/Constant';
import {
  clearGrids,
  fetchGridsOfVillage,
  fetchVillagesOfDistricts,
} from '../../features/locationFilterSlice';
import { toast } from 'react-toastify';

/**
 * This function returns the selected year along with the next year, e.g., "2024-2025".
 */
export const getCurrentFinancialYear = (
  selectedFiltersData: FilterType,
  yearOptions: SelectOptionType[]
) => {
  if (selectedFiltersData?.year) {
    const year = yearOptions?.filter(
      (year: any) => year.value === selectedFiltersData?.year
    )?.[0]?.label;
    if (year) {
      const currentYear = moment(year, 'YYYY').year();
      const nextYear = moment(year, 'YYYY').add(1, 'year').format('YY');
      return `${currentYear}-${nextYear}`;
    } else {
      return '-';
    }
  } else {
    return '-';
  }
};

/**
 * This function returns the selected year along with the previous year, e.g., "2023-2024".
 */
export const getPreviousFinancialYear = (
  selectedFiltersData: FilterType,
  yearOptions: SelectOptionType[]
) => {
  if (selectedFiltersData?.year) {
    const year = yearOptions?.filter(
      (year: any) => year.value === selectedFiltersData?.year
    )?.[0]?.label;

    if (year) {
      const currentYear = moment(year, 'YYYY').format('YY');
      const prevYear = moment(year, 'YYYY').subtract(1, 'year').year();
      return `${prevYear}-${currentYear}`;
    } else {
      return '-';
    }
  } else {
    return '-';
  }
};

/**
 * Calculates the total number of targets and formats the result to '0.00' for better readability.
 */
export const formatNumber = (type: string, globalData: TargetType[]) => {
  let prevValue = 0;
  let curValue = 0;
  if (globalData?.length) {
    globalData.forEach((item: any) => {
      item?.targets?.forEach((target: any) => {
        prevValue += Number(target?.targetValuePreviousYear);
        curValue += Number(target?.targetValueCurrentYear);
      });
    });
  }
  const num = type === 'prev' ? prevValue : curValue;
  return num ? num.toFixed(2) + ' INR' : '0.00 INR';
};

// Updated createPayloadFilteredValues
const createPayloadFilteredValues = (
  selectedFiltersData: FilterType,
  item: any
) => {
  return {
    seasonId: Number(selectedFiltersData?.season) || null,
    targetTypeId: Number(selectedFiltersData?.target) || null,
    financialYearId: Number(selectedFiltersData.year) || null,
    zoneId: item?.zoneId || null,
    stateId: item?.stateId || null,
    districtId: item?.districtId || null,
    // Use values from item when drilling down
    cityId: item?.cityId ? [item?.cityId] : null,
    gridId: item?.gridId ? [item?.gridId] : null,
    productId: Number(selectedFiltersData?.product) || 0,
  };
};

/**
 * Creates the payload and sets the filters when the target name in the table is clicked.
 */
export const handleTitleClick = (
  selectedFiltersData: any,
  setTargetState: (data: any) => void,
  setTableFilters: (data: any) => void,
  getTargetList: (filteredValues: any) => void,
  item: any
) => {
  const payloadFilteredValues = createPayloadFilteredValues(
    selectedFiltersData,
    item
  );

  if (item?.gridId) {
    setTargetState({ value: item?.gridId, label: 'GridWise' });
  } else if (item?.cityId) {
    setTargetState({ value: item?.cityName, label: 'Village' });
  } else if (item?.districtId) {
    setTargetState({ value: item?.districtName, label: 'District' });
  } else if (item?.stateId) {
    setTargetState({ value: item?.stateName, label: 'State' });
  } else if (item?.zoneId) {
    setTargetState({ value: item?.zoneName, label: 'Zone' });
  }

  const targetFilter = {
    season: Number(selectedFiltersData?.season) || null,
    year: Number(selectedFiltersData.year) || null,
    zone: item?.zoneId || null,
    state: item?.stateId ?? 'All',
    district: item?.districtId ?? 'All',
    village: item?.cityId ? [item?.cityId] : 'All',
    grid: item?.gridId ? [item?.gridId] : 'All',
    target: Number(selectedFiltersData?.target) || 'All',
    product: Number(selectedFiltersData?.product) || 0,
  };

  setTableFilters(targetFilter);
  getTargetList(payloadFilteredValues);
};

/**
 * Returns the target name based on the selected location filter.
 */
export const handleAllIndiaData = (item: any) => {
  if (item?.gridId) {
    return item?.gridId;
  } else if (item?.cityId) {
    return item?.cityName;
  } else if (item?.districtId) {
    return item?.districtName;
  } else if (item?.stateId) {
    return item?.stateName;
  } else if (item?.zoneId) {
    return item?.zoneName;
  }
  return '-';
};

/**
 * Handles the onChange event while updating the targets and also restricting to enter more than 6 digits.
 */
export const onCurrentTargetChanged = (
  e: any,
  index: number,
  targetData: TargetType[],
  targetUpdateData: any,
  globalData: TargetType[],
  targetIndex: number,
  setters: {
    setTargetData: (data: TargetType[]) => void;
    setTargetUpdateData: (data: any) => void;
  }
) => {
  const { setTargetData, setTargetUpdateData } = setters;
  let value = e.target.value.replace(/[^0-9-]/g, '');

  if (value?.length <= 10) {
    const targetDataOld = [...targetData];
    const subTarget = [...targetData[index].targets];
    subTarget[targetIndex] = {
      ...subTarget[targetIndex],
      targetValueCurrentYear: value.replace('-', ''),
    };
    targetDataOld[index] = {
      ...targetDataOld[index],
      targets: [...subTarget],
    };
    setTargetData([...targetDataOld]); // updating new value form input
    if (value.length >= 1) {
      // default value is '-' by default length will be 1
      value = value.replace('-', '');
      value = Number(value);
    }
    const targetUpdateDataTemp: any = targetUpdateData ?? {};
    const previousValue =
      globalData[index].targets[targetIndex].targetValueCurrentYear;
    if (isValidString(value) && previousValue !== value) {
      const newUpdateData = targetDataOld[index];
      newUpdateData.targets[targetIndex].targetValueCurrentYear = Number(value);
      targetUpdateDataTemp[index] = newUpdateData;
      setTargetUpdateData(targetUpdateDataTemp);
    } else if (
      isValidString(previousValue) &&
      !isValidString(value) &&
      previousValue !== value
    ) {
      const newUpdateData = targetDataOld[index];
      newUpdateData.targets[targetIndex].targetValueCurrentYear = null;
      targetUpdateDataTemp[index] = newUpdateData;
      setTargetUpdateData(targetUpdateDataTemp);
    } else if (
      (!isValidString(previousValue) && !isValidString(value)) ||
      targetUpdateData?.[index]
    ) {
      const dataAvailable = targetUpdateDataTemp;
      delete dataAvailable[index];
      setTargetUpdateData(dataAvailable);
    }
  }
};

/**
 * Constructs the payload for the POST API request to set the targets.
 */
export const getPayloadData = (targetData: TargetType[]) => {
  const data = targetData?.map((item: TargetType) => {
    return {
      seasonId: item?.seasonId,
      targetTypeId: item?.targetTypeId,
      financialYearId: item?.financialYearId,
      zoneId: item?.zoneId,
      stateId: item?.stateId,
      districtId: item?.districtId,
      cityId: item?.cityId,
      gridId: item?.gridId,
      yearlyTargetId: item?.yearlyTargetId,
      targets: item?.targets?.map((target: Target) => ({
        productId: target.productId,
        targetValue: target.targetValueCurrentYear,
      })),
    };
  });
  return data;
};

/**
 * Creates the payload for fetching target data from the API.
 *
 * @returns {Object} The payload object containing the necessary parameters for the API request.
 */
export const getTargetPayload = (
  selectedFiltersData: FilterType,
  filteredValues: any,
  setTableFilters: (data: FilterType) => void,
  TARGET_FILTER_INITT: any
) => {
  let payload: any = {};
  const data = {
    seasonId: Number(selectedFiltersData?.season) || null,
    targetTypeId: Number(selectedFiltersData?.target) || null,
    financialYearId: Number(selectedFiltersData?.year) || null,
    zoneId: Number(selectedFiltersData?.zone) || null,
    stateId: Number(selectedFiltersData?.state) || null,
    districtId: Number(selectedFiltersData?.district) || null,
    cityId:
      Array.isArray(selectedFiltersData?.village) &&
      selectedFiltersData?.village.length > 0
        ? selectedFiltersData.village
        : null,
    gridId: Array.isArray(selectedFiltersData?.grid)
      ? selectedFiltersData?.grid
      : null,
    productId: Number(selectedFiltersData?.product) || 0,
  };
  if (filteredValues) {
    payload = { ...filteredValues };
  } else {
    payload = { ...data };
    setTableFilters(TARGET_FILTER_INITT);
  }
  return payload;
};

/**
 * Validates the targets before submission.
 */
export const checkHandlePostTargetList = (
  targetUpdateData: any,
  targetData: TargetType[]
) => {
  if (!targetUpdateData) {
    return null;
  }
  const sumOfTargets: any = {};
  const countOfTargets: any = {};

  targetData.forEach((item: TargetType) => {
    item.targets.forEach(({ productId, targetValueCurrentYear }: Target) => {
      sumOfTargets[productId] =
        (sumOfTargets[productId] ?? 0) + Number(targetValueCurrentYear);

      if (!!targetValueCurrentYear || targetValueCurrentYear === 0) {
        countOfTargets[productId] = (countOfTargets[productId] ?? 0) + 1;
      }
    });
  });
  const intialTargetsCount = targetData[0]?.targets;
  let errorMsg = '';
  intialTargetsCount?.forEach((target: Target) => {
    if (
      target?.higherValues &&
      sumOfTargets[target?.productId] < target?.higherValues &&
      countOfTargets[target?.productId] &&
      targetData?.length === countOfTargets[target?.productId]
    ) {
      // eslint-disable-next-line max-len
      errorMsg = `The provided targets must be greater than or equal to the assigned targets for this product, ${target?.productName}.`;
    }
  });
  return errorMsg;
};

/**
 * Makes a POST request to the Targets API.
 */
export const handlePostTargetList = (
  targetUpdateData: any,
  targetData: TargetType[],
  minTargetCount: number | null,
  options: {
    setLoading: (isLoading: boolean) => void;
    getTargetList: (filteredValues?: any) => void;
    setGlobalData: (data: TargetType[]) => void;
    setTargetUpdateData: (data: any) => void;
  },
  toast: any,
  dispatch: ThunkDispatch<any, any, any>
) => {
  const { setLoading, setGlobalData, setTargetUpdateData, getTargetList } =
    options;
  const value = checkHandlePostTargetList(targetUpdateData, targetData);
  if (!value) {
    const data = getPayloadData(targetData);
    setLoading(true);
    dispatch(postTargetList(data))
      .then((fulfilled: any) => {
        setLoading(false);
        if (isAPISuccess(fulfilled.payload)) {
          toast.success(fulfilled.payload.responseInformation.responseMessage);
          setTargetUpdateData(undefined);
          getTargetList();
          setGlobalData(targetData);
        } else {
          toast.error(fulfilled.payload.responseInformation.responseMessage);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  } else {
    toast.error(value);
  }
};

/**
 * On Village/City value change from dropdown
 */
export const onVillageChange = (
  event: any,
  selectedFiltersData: any,
  setSelectedFiltersData: (value: any) => void,
  dispatch: ThunkDispatch<any, any, any>
) => {
  const value = Array.isArray(event) ? event : [event]; // Ensure value is always an array
  setSelectedFiltersData({
    ...selectedFiltersData,
    village: value, // Store multiple selected villages
    grid: TARGET_FILTER_INIT.grid,
  });

  dispatch(clearGrids());

  if (value.length > 0) {
    dispatch(
      fetchGridsOfVillage({
        villageId: value,
        pincode: null,
        candidateId: null,
      })
    );
  }
};

/**
 * On District value  change from dropdown
 */
export const onDistrictChange = (
  event: any,
  selectedFiltersData: any,
  cityOptions: MapOptionType,
  setSelectedFiltersData: (value: any) => void,
  dispatch: ThunkDispatch<any, any, any>
) => {
  const value = event.target.value;
  setSelectedFiltersData({
    ...selectedFiltersData,
    district: value,
    village: TARGET_FILTER_INIT.village,
    grid: TARGET_FILTER_INIT.grid,
  });
  if (value && !cityOptions[value]) {
    dispatch(
      fetchVillagesOfDistricts({
        districtId: [Number(value)],
      })
    );
  }
};

/**
 * On state value change from dropdown
 * @param event
 */
export const onStateChange = (
  event: any,
  selectedFiltersData: any,
  districtData: MapOptionType,
  setSelectedFiltersData: (value: any) => void,
  setDistrictOption: (value: SelectOptionType[]) => void
) => {
  const value = event.target.value;
  setSelectedFiltersData({
    ...selectedFiltersData,
    state: value,
    district: TARGET_FILTER_INIT.district,
    village: TARGET_FILTER_INIT.village,
    grid: TARGET_FILTER_INIT.grid,
  });
  setDistrictOption(
    value && districtData[value]
      ? [DEFAULT_OPTION_LOCATION_SET_TARGET, ...districtData[value]]
      : [DEFAULT_OPTION_LOCATION_SET_TARGET]
  );
};

/**
 * On Zone value change from dropdown
 */
export const onZoneChange = (
  event: any,
  selectedFiltersData: any,
  allStates: MapOptionType,
  setSelectedFiltersData: (value: any) => void,
  setStateArrayData: (value: SelectOptionType[]) => void
) => {
  const value: any = event.target.value;
  setSelectedFiltersData({
    ...selectedFiltersData,
    zone: value,
    state: TARGET_FILTER_INIT.state,
    district: TARGET_FILTER_INIT.district,
    village: TARGET_FILTER_INIT.village,
    grid: TARGET_FILTER_INIT.grid,
  });
  setStateArrayData(allStates[value]);
};

/**
 * Returns the option that corresponds to the specified ID from the options list.
 */
const getTitleOfActiveDropName = (options: any, id: any) => {
  const result = options?.filter((opt: any) => {
    return String(opt.value) == String(id);
  });
  return result?.[0] || null;
};

/**
 * Validates the filters applied to the data and Makes an API call to fetch target list API.
 */

export const handleApplyButton = (
  selectedFiltersData: any,
  zoneOptions: SelectOptionType[],
  stateOption: SelectOptionType[],
  districtOption: SelectOptionType[],
  cityOptions: MapOptionType,
  onApply: (value: any) => void
) => {
  if (!selectedFiltersData.year) {
    toast.error('Year is required');
  } else if (!selectedFiltersData.season) {
    toast.error('Season is required');
  } else if (!selectedFiltersData.target) {
    toast.error('Sales Target Type is required');
  } else {
    let val: { value: nsnType[] | nsnType; label: string };

    if (
      selectedFiltersData?.grid &&
      selectedFiltersData?.grid !== 'All' &&
      selectedFiltersData?.grid.length == 1
    ) {
      val = { label: 'GridWise', value: selectedFiltersData?.grid };
    } else if (
      selectedFiltersData?.village &&
      Array.isArray(selectedFiltersData?.village) &&
      selectedFiltersData?.village.length > 0
    ) {
      const villageNames: any = selectedFiltersData.village.map(
        (villageId: nsnType) =>
          getTitleOfActiveDropName(
            cityOptions?.[selectedFiltersData?.district],
            villageId
          )?.label
      );
      if (villageNames?.length == 1) {
        val = { label: 'Village', value: villageNames };
      } else {
        val = { label: 'Villages', value: '' };
      }
    } else if (
      selectedFiltersData?.district &&
      selectedFiltersData?.district !== 'All'
    ) {
      const name = getTitleOfActiveDropName(
        districtOption,
        selectedFiltersData?.district
      );
      val = { label: 'District', value: name?.label };
    } else if (
      selectedFiltersData?.state &&
      selectedFiltersData?.state !== 'All'
    ) {
      const name = getTitleOfActiveDropName(
        stateOption,
        selectedFiltersData?.state
      );
      val = { label: 'State', value: name?.label };
    } else {
      const name = getTitleOfActiveDropName(
        zoneOptions,
        selectedFiltersData?.zone
      );
      val = { label: 'Zone', value: name?.label };
    }
    onApply(val);
  }
};
